<template>
  <el-card shadow="never" class="analysis-status-card">
    <div slot="header" class="panel-header">
      <div>
        <div class="panel-title">{{ title }}</div>
        <div class="panel-subtitle">{{ subtitle }}</div>
      </div>
      <div class="panel-actions">
        <el-tag size="small" :type="statusTagType">{{ statusLabel }}</el-tag>
        <el-button v-if="refreshable" size="mini" icon="el-icon-refresh" :loading="loading" @click="$emit('refresh')">刷新状态</el-button>
      </div>
    </div>

    <el-alert
      :title="headline"
      :type="alertType"
      :closable="false"
      show-icon
      class="status-alert"
    />

    <div class="metrics-grid">
      <div class="metric-item">
        <span>分析范围</span>
        <strong>{{ memberCount }}</strong>
        <small>{{ scopeSummary }}</small>
      </div>
      <div class="metric-item">
        <span>日线数据</span>
        <strong>{{ dailySummary }}</strong>
        <small>长期数据 · 周/月由日线自动生成</small>
      </div>
      <div class="metric-item">
        <span>5分钟数据</span>
        <strong>{{ fiveSummary }}</strong>
        <small>热数据 · 15分钟/60分钟由5分钟自动生成</small>
      </div>
      <div class="metric-item">
        <span>最新完整交易日</span>
        <strong>{{ latestTradeDay }}</strong>
        <small>{{ currentDayText }}</small>
      </div>
    </div>

    <el-table
      v-if="showItems"
      :data="rows"
      border
      stripe
      size="small"
      class="status-table"
      empty-text="暂无分析数据状态"
    >
      <el-table-column prop="ts_code" label="代码" width="112" />
      <el-table-column v-if="showName" prop="name" label="名称" min-width="100" />
      <el-table-column v-if="showZone" label="区域" width="88">
        <template slot-scope="{ row }">{{ zoneLabel(row.zone) }}</template>
      </el-table-column>
      <el-table-column label="当前状态" width="150">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="itemStatusTagType(row.status)">{{ itemStatusLabel(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="日线" width="130">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="itemStatusTagType((row.daily || {}).status)">{{ sourceStatusLabel((row.daily || {}).status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="周 / 月" min-width="190">
        <template slot-scope="{ row }">
          <div class="derived-line">
            <span>周</span>
            <el-tag size="mini" :type="derivedTagType(((row.derived || {}).weekly || {}).status)">{{ derivedLabel(((row.derived || {}).weekly || {}).status) }}</el-tag>
          </div>
          <div class="derived-line">
            <span>月</span>
            <el-tag size="mini" :type="derivedTagType(((row.derived || {}).monthly || {}).status)">{{ derivedLabel(((row.derived || {}).monthly || {}).status) }}</el-tag>
          </div>
          <div v-if="derivedNote((row.derived || {}).weekly)" class="derived-source-note">{{ derivedNote((row.derived || {}).weekly) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="5分钟" width="130">
        <template slot-scope="{ row }">
          <el-tag size="mini" :type="itemStatusTagType((row.five_minute || {}).status)">{{ sourceStatusLabel((row.five_minute || {}).status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="15分钟 / 60分钟" min-width="210">
        <template slot-scope="{ row }">
          <div class="derived-line">
            <span>15分钟</span>
            <el-tag size="mini" :type="derivedTagType(((row.derived || {})['15m'] || {}).status)">{{ derivedLabel(((row.derived || {})['15m'] || {}).status) }}</el-tag>
          </div>
          <div class="derived-line">
            <span>60分钟</span>
            <el-tag size="mini" :type="derivedTagType(((row.derived || {})['60m'] || {}).status)">{{ derivedLabel(((row.derived || {})['60m'] || {}).status) }}</el-tag>
          </div>
          <div v-if="derivedNote((row.derived || {})['15m'])" class="derived-source-note">{{ derivedNote((row.derived || {})['15m']) }}</div>
        </template>
      </el-table-column>
    </el-table>

    <div class="boundary-note">
      <i class="el-icon-info"></i>
      <span>页面只判断“现在能不能分析”。数据是否全部齐全不等同于分析可用性；当前形成中的交易日不会被当作缺口。</span>
    </div>
  </el-card>
</template>

<script>
import {
  analysisStatusRows,
  derivedStatusLabel,
  derivedStatusTagType,
  derivedSourceNote,
  productStatusLabel,
  productStatusTagType
} from '@/utils/analysisDataStatus'

export default {
  name: 'AnalysisDataStatusPanel',
  props: {
    payload: { type: Object, default: () => ({}) },
    title: { type: String, default: '分析数据状态' },
    subtitle: { type: String, default: '只展示分析是否可用，内部运维细节由后台治理。' },
    loading: { type: Boolean, default: false },
    refreshable: { type: Boolean, default: true },
    showItems: { type: Boolean, default: true },
    showName: { type: Boolean, default: true },
    showZone: { type: Boolean, default: false }
  },
  computed: {
    status() { return (this.payload || {}).analysis_data_status || {} },
    statusKnown() {
      return Boolean(this.status && ['ready', 'warming', 'stale', 'unavailable', 'quality_blocked'].includes(String(this.status.status || '')))
    },
    rows() { return this.statusKnown ? analysisStatusRows(this.payload) : [] },
    statusLabel() { return this.statusKnown ? productStatusLabel(this.status.status) : '状态待确认' },
    statusTagType() { return this.statusKnown ? productStatusTagType(this.status.status) : 'info' },
    alertType() {
      if (!this.statusKnown) return 'warning'
      if (this.status.status === 'ready') return 'success'
      if (['warming', 'stale'].includes(this.status.status)) return 'warning'
      return 'error'
    },
    headline() {
      if (!this.statusKnown) return '分析数据状态暂不可确认，请稍后刷新。'
      if (this.status.status === 'ready') return '当前分析数据正常，可进行完整分析。'
      if (this.status.status === 'stale') return '当前仍可分析，但数据处于降级状态，请留意最新完整交易日。'
      if (this.status.status === 'warming') return '分析数据正在准备，部分标的暂时不能完整分析。'
      if (this.status.status === 'quality_blocked') return '发现数据质量问题，需要处理后再进行完整分析。'
      return '当前暂不可完整分析，请稍后刷新或检查数据状态。'
    },
    memberCount() { return this.statusKnown ? Number((this.status.hot_set || {}).member_count || this.status.target_count || 0) : '—' },
    stockCount() { return this.statusKnown ? Number((this.status.hot_set || {}).stock_count || 0) : null },
    indexCount() { return this.statusKnown ? Number((this.status.hot_set || {}).core_index_count || 0) : null },
    scopeSummary() {
      if (!this.statusKnown) return '当前状态未确认'
      return `${this.stockCount} 只股票 · ${this.indexCount} 个核心指数`
    },
    dailyReady() { return this.statusKnown ? Number((this.status.daily || {}).ready_count || 0) : null },
    dailyTarget() { return this.statusKnown ? Number((this.status.daily || {}).target_count || Number(this.memberCount || 0)) : null },
    dailySummary() { return this.statusKnown ? `${this.dailyReady} / ${this.dailyTarget}` : '— / —' },
    fiveReady() { return this.statusKnown ? Number((this.status.five_minute || {}).ready_count || 0) : null },
    fiveTarget() { return this.statusKnown ? Number((this.status.five_minute || {}).target_count || Number(this.memberCount || 0)) : null },
    fiveSummary() { return this.statusKnown ? `${this.fiveReady} / ${this.fiveTarget}` : '— / —' },
    latestTradeDay() { return this.statusKnown ? (this.status.latest_completed_trade_day || '—') : '—' },
    currentDayText() {
      if (!this.statusKnown) return '按最近完整交易日判断'
      const current = this.status.current_trade_day || {}
      if (current.today_forming_not_gap === true) return '今日交易日尚未完成，不计为缺口'
      if (current.today_completed === true) return '今日已完成，可纳入分析数据判断'
      return '按最近完整交易日判断'
    }
  },
  methods: {
    itemStatusLabel(status) { return productStatusLabel(status) },
    itemStatusTagType(status) { return productStatusTagType(status) },
    sourceStatusLabel(status) {
      const value = String(status || '').toLowerCase()
      if (value === 'ready') return '数据正常'
      if (value === 'stale') return '数据降级'
      if (value === 'warming') return '正在准备'
      if (value === 'quality_blocked') return '需要处理'
      if (value === 'unavailable') return '暂不可用'
      return '待确认'
    },
    derivedLabel(status) { return derivedStatusLabel(status) },
    derivedTagType(status) { return derivedStatusTagType(status) },
    derivedNote(item) { return derivedSourceNote(item) },
    zoneLabel(zone) { return ({ formal: '正式区', temp: '临时区' })[zone] || zone || '—' }
  }
}
</script>

<style scoped>
.analysis-status-card { border-radius: 16px; border-color: #dfe7f2; }
.panel-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.panel-title { color: #1f2937; font-size: 18px; font-weight: 700; }
.panel-subtitle { margin-top: 5px; color: #7b8798; font-size: 12px; line-height: 1.55; }
.panel-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.status-alert { margin-bottom: 14px; }
.metrics-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 14px; }
.metric-item { padding: 14px; border: 1px solid #e8eef5; border-radius: 14px; background: #f8fbff; }
.metric-item span { color: #64748b; font-size: 12px; }
.metric-item strong { display: block; margin: 6px 0 4px; color: #0f172a; font-size: 22px; }
.metric-item small { color: #94a3b8; font-size: 11px; line-height: 1.45; }
.status-table { width: 100%; }
.derived-line { display: flex; align-items: center; gap: 6px; margin: 3px 0; }
.derived-line span { min-width: 42px; color: #64748b; font-size: 11px; }
.derived-source-note { margin: 5px 0; color: #9a6700; font-size: 11px; line-height: 1.5; }
.boundary-note { display: flex; align-items: flex-start; gap: 8px; margin-top: 12px; padding: 10px 12px; border-radius: 10px; background: #f6f8fb; color: #68778b; font-size: 12px; line-height: 1.6; }
@media (max-width: 1100px) {
  .panel-header { flex-direction: column; }
  .metrics-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
